import { useQuery, useQueryCache } from '@pinia/colada';
import { defineStore } from 'pinia';
import { computed, getCurrentInstance, ref, watch } from 'vue';

import { useAccess } from '@/composables/useAccess';
import { fetchPoDetail, fetchTrackerList, fetchTrackerUpdates, markTrackerSeen } from '@/services/poTrackerService';
import { PERMISSIONS } from '@/types/access';
import { PO_TRACKER_TAB, type PoDetail, type PoTrackerList, type TrackerUpdates } from '@/types/poTracker';
import { extractErrorMessage } from '@/utils/apiError';
import { canAnswerRequests } from '@/utils/poTracker';
import { isFullPoNumber, type TrackerUrlState } from '@/utils/poTrackerQuery';

const FIRST_PAGE = 1;

const DEFAULT_URL: TrackerUrlState = {
  vendorId: null,
  tab: PO_TRACKER_TAB.ALL,
  po: '',
  page: FIRST_PAGE,
};

const RESETS_PAGE: readonly (keyof TrackerUrlState)[] = ['vendorId', 'tab', 'po'];

// Every tracker query is keyed under the vendor, so a vendor's whole cache can be invalidated at once.
export const PO_TRACKER_QUERY_KEY = 'po-tracker';
// Tracker data changes under the vendor's hands: every navigation, expand and window focus refetches, cached rows showing meanwhile.
export const ALWAYS_STALE = 0;
const LIST_SEGMENT = 'list';
const PO_SEGMENT = 'po';
const UPDATES_SEGMENT = 'updates';
const VENDOR_INDEX = 1;

export const poDetailKey = (vendorId: string, poNumber: string): [string, string, string, string] => [PO_TRACKER_QUERY_KEY, vendorId, PO_SEGMENT, poNumber];
export const poDetailQuery = (vendorId: string, poNumber: string): Promise<PoDetail> => fetchPoDetail(vendorId, poNumber, true);

export const usePoTrackerStore = defineStore('poTracker', () => {
  // A query created inside a component is hooked to that component's lifecycle by pinia/colada and stops
  // refetching (writes, focus, reconnect) once it unmounts, though the store lives on. main.ts creates the
  // store at boot, so the queries belong to the store's own scope and stay live for every page visit.
  if (getCurrentInstance()) throw new Error('usePoTrackerStore must first be called outside a component (see main.ts)');

  const queryCache = useQueryCache();
  const { hasPermission } = useAccess();
  const url = ref<TrackerUrlState>({ ...DEFAULT_URL });
  // Set by the tracker page while it is mounted: the queries run only then, so a focus or a reconnect on
  // another page doesn't refetch the tracker, and reopening the page refetches.
  const pageOpen = ref(false);
  // Collections are replaced, never mutated, so every change is observable.
  const expanded = ref<Set<string>>(new Set());
  // The PO whose delivery request answer form is open; one at a time.
  const answering = ref<string | null>(null);

  const planner = computed(() => hasPermission(PERMISSIONS.REQUEST_PO_DELIVERY));
  const listEnabled = computed(() => pageOpen.value && url.value.vendorId !== null);

  // The list refetches on the window regaining focus and on reconnect (pinia/colada defaults).
  const listQuery = useQuery<PoTrackerList>({
    key: () => [PO_TRACKER_QUERY_KEY, url.value.vendorId ?? '', LIST_SEGMENT, url.value.tab, url.value.po, url.value.page],
    query: () => {
      const { vendorId, tab, po, page } = url.value;
      return fetchTrackerList({ vendorId: vendorId as string, tab, search: po || undefined, page });
    },
    enabled: listEnabled,
    staleTime: ALWAYS_STALE,
    // The previous rows stay on screen while a tab, search or page loads; another vendor's rows never show, even for a moment.
    placeholderData: (previous, previousEntry) => (previousEntry?.key[VENDOR_INDEX] === url.value.vendorId ? previous : undefined),
  });
  // The rows the vendor last answered with, so a tab, search or page that fails to load keeps them on screen behind the banner.
  const lastList = ref<PoTrackerList | null>(null);
  watch(listQuery.data, (data) => {
    if (data !== undefined && !listQuery.isPlaceholderData.value) lastList.value = data;
  });
  const list = computed<PoTrackerList | null>(() => listQuery.data.value ?? lastList.value);
  // The search the rows on screen answer; null while they are an earlier search's rows.
  const listPo = computed<string | null>(() => (listQuery.data.value !== undefined && !listQuery.isPlaceholderData.value ? url.value.po : null));
  const loading = computed(() => listQuery.isLoading.value);
  // The request is inline: the page shows this in its banner with Retry (a 403 is toasted too).
  const error = computed(() => (listQuery.error.value ? extractErrorMessage(listQuery.error.value) : null));

  // Planner-only, and the single-PO view shows none. The key ignores the search so the box doesn't flicker on every keystroke.
  const updatesEnabled = computed(() => pageOpen.value && planner.value && url.value.vendorId !== null && !isFullPoNumber(url.value.po));
  const updatesQuery = useQuery<TrackerUpdates>({
    key: () => [PO_TRACKER_QUERY_KEY, url.value.vendorId ?? '', UPDATES_SEGMENT],
    query: () => fetchTrackerUpdates(url.value.vendorId as string),
    enabled: updatesEnabled,
    staleTime: ALWAYS_STALE,
  });
  // A failed load keeps the previous box; the global interceptor already toasted.
  const updates = computed<TrackerUpdates | null>(() => updatesQuery.data.value ?? null);

  function collapse(poNumber: string): void {
    const rest = new Set(expanded.value);
    rest.delete(poNumber);
    expanded.value = rest;
    if (answering.value === poNumber) answering.value = null;
  }

  function toggle(poNumber: string): void {
    if (expanded.value.has(poNumber)) collapse(poNumber);
    else expanded.value = new Set(expanded.value).add(poNumber);
  }

  // The backend refuses a planner's answer; the form must not open for one either.
  function startAnswer(poNumber: string): void {
    if (!canAnswerRequests(hasPermission)) return;
    answering.value = poNumber;
  }

  function setUrl(patch: Partial<TrackerUrlState>): void {
    const prev = url.value;
    const next = { ...prev, ...patch };
    const changed = (key: keyof TrackerUrlState): boolean => next[key] !== prev[key];
    if (RESETS_PAGE.some(changed)) next.page = patch.page ?? FIRST_PAGE;
    if (changed('vendorId') || changed('po')) {
      expanded.value = new Set();
      answering.value = null;
    }
    if (changed('vendorId')) lastList.value = null;
    url.value = next;
  }

  // Fresh rows for the current URL; a fetch already running is reused.
  async function loadList(): Promise<void> {
    if (!listEnabled.value) return;
    await listQuery.refresh();
  }

  async function loadUpdates(): Promise<void> {
    if (!updatesEnabled.value) return;
    await updatesQuery.refresh();
  }

  function cachedDetail(poNumber: string): PoDetail | undefined {
    return queryCache.getQueryData<PoDetail>(poDetailKey(url.value.vendorId ?? '', poNumber));
  }

  // A collapsed PO's cache is dropped so reopening it fetches instead of showing the old state. An expanded PO is still on screen and is refetched instead.
  function dropDetail(poNumber: string): void {
    queryCache.getEntries({ key: poDetailKey(url.value.vendorId ?? '', poNumber), exact: true, active: false }).forEach((entry) => queryCache.remove(entry));
  }

  // The list refetches too so the Updated tags and tab count clear.
  async function markSeen(): Promise<void> {
    const vendorId = url.value.vendorId;
    if (!vendorId) return;
    await markTrackerSeen(vendorId);
    await queryCache.invalidateQueries({ key: [PO_TRACKER_QUERY_KEY, vendorId] }).catch(() => undefined);
  }

  // The write succeeded; a refetch that fails shows in the list banner or the PO's panel, never here.
  async function afterWrite(poNumbers: string[]): Promise<void> {
    const vendorId = url.value.vendorId;
    if (!vendorId) return;
    poNumbers.filter((poNumber) => !expanded.value.has(poNumber)).forEach(dropDetail);
    await Promise.all([
      queryCache.invalidateQueries({ key: [PO_TRACKER_QUERY_KEY, vendorId, LIST_SEGMENT] }),
      queryCache.invalidateQueries({ key: [PO_TRACKER_QUERY_KEY, vendorId, PO_SEGMENT] }),
    ]).catch(() => undefined);
  }

  return {
    url,
    pageOpen,
    list,
    listPo,
    loading,
    error,
    expanded,
    answering,
    updates,
    startAnswer,
    setUrl,
    loadList,
    loadUpdates,
    cachedDetail,
    dropDetail,
    collapse,
    markSeen,
    toggle,
    afterWrite,
  };
});
